import { Icon } from '@/components/ui';
import { useSipStore } from '../stores/sipStore';

/**
 * פס מצב השיחה במרכז מסך העבודה על קמפיין.
 *
 * 06/10/2026 (דורון): "לא כל כך ברור מי שענה, אם הוא ניתק או לא — זה צריך
 * להיות במיקום יותר מרכזי". עד כאן המידע הזה היה מפוזר: שם הליד בפאנל הימני,
 * מצב הקו בטקסט קטן בכותרת, והניתוק — בשום מקום. כאן הוא בפס אחד, גדול
 * וצבעוני, מעל שלושת הפאנלים: מי על הקו, כמה זמן, ומי ניתק.
 */

function mmss(sec: number): string {
  const s = Math.max(0, Math.floor(sec));
  return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
}

export function CallStatusBanner({ name, phone }: { name?: string | null; phone?: string | null }) {
  const state = useSipStore((s) => s.state);
  const active = useSipStore((s) => s.active);
  const who = name?.trim() || 'הלקוח';
  const number = active?.number || phone || '';

  let tone: 'live' | 'ringing' | 'ended' | null = null;
  let title = '';
  let detail = '';

  if (state === 'on-call' || state === 'on-hold') {
    tone = 'live';
    title = `${who} ענה — על הקו`;
    detail = state === 'on-hold' ? 'בהמתנה' : mmss(active?.durationSec ?? 0);
  } else if (state === 'ringing-in' || state === 'ringing-out') {
    tone = 'ringing';
    title = `${who} ענה — מחבר אליך…`;
  } else if (state === 'wrap-up' && active) {
    tone = 'ended';
    title = active.endedBy === 'local' ? `ניתקת את השיחה עם ${who}` : `${who} ניתק את השיחה`;
    detail = `משך ${mmss(active.durationSec ?? 0)} · בחר קוד סיום ושמור כדי להמשיך`;
  }

  if (!tone) return null;

  const palette = {
    live: { bg: 'var(--ok)', fg: 'var(--on-accent, #fff)', icon: 'phone' },
    ringing: { bg: 'var(--warn)', fg: 'var(--on-accent, #fff)', icon: 'phoneIn' },
    ended: { bg: 'var(--danger)', fg: 'var(--on-accent, #fff)', icon: 'hang' },
  }[tone];

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="call-status-banner"
      data-tone={tone}
      style={{
        margin: 'var(--gap) var(--gap) 0', padding: '14px 18px',
        borderRadius: 'var(--r-3)', background: palette.bg, color: palette.fg,
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 14,
        flexWrap: 'wrap', textAlign: 'center', boxShadow: 'var(--shadow-2)',
      }}
    >
      <Icon name={palette.icon} size={22} color="currentColor" />
      <strong style={{ fontSize: 'var(--t-xl)' }}>{title}</strong>
      {number && <span dir="ltr" style={{ fontSize: 'var(--t-lg)', opacity: 0.95 }}>{number}</span>}
      {detail && <span style={{ fontSize: 'var(--t-md)', opacity: 0.95 }}>{detail}</span>}
    </div>
  );
}
